import { useCallback, useContext, useEffect, useState } from "react";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Loader,
} from "@transfr-inc/dashboard-components";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";
import { SearchBox, SortHeader } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";

import { IntegrationsContext } from "@src/context";

import { ProviderDropdown } from "./components/provider.dropdown";
import { StatusDropdown } from "./components/status.dropdown";
import SyncHistoryItem from "./components/sync-history-item";
import SyncIntegrationErrorsModal from "./components/sync-integration-errors.modal";
import { useIntegrationsSortHeader } from "./hooks/use-integrations-sort-header";

import "./index.scss";

const query = {
  "small-panel": {
    minWidth: 320,
    maxWidth: 850,
  },
  "medium-panel": {
    minWidth: 850,
    maxWidth: 1150,
  },
  "large-panel": {
    minWidth: 1150,
  },
};

export const IntegrationsList = ({ responsiveStyles }) => {
  const [params, containerRef] = useContainerQuery(query);
  const [filters, setFilters] = useState({
    orgName: null,
    status: null,
    provider: null,
  });
  const [filteredIntegrations, setFilteredIntegrations] = useState([]);
  const [syncErrors, setSyncErrors] = useState(null);

  const { integrations, integrationsCount, loadingIntegrations } =
    useContext(IntegrationsContext);

  const onSearchTermChange = (term) => {
    setFilters((prev) => ({ ...prev, orgName: term?.toLowerCase() }));
  };

  const applyFilters = useCallback(
    (term) => {
      const orgSearchTerm = term?.toLowerCase() ?? filters.orgName;
      const filterFunctions = [
        (integration) =>
          !orgSearchTerm ||
          integration.organizationName.toLowerCase().includes(orgSearchTerm) ||
          integration.parentOrganizationName
            ?.toLowerCase()
            ?.includes(orgSearchTerm),
        (integration) =>
          !filters.status ||
          integration.status.toLowerCase() === filters.status.toLowerCase(),
        (integration) =>
          !filters.provider ||
          integration.idp.typeId === filters.provider.typeId,
      ];

      return integrations?.filter((integration) =>
        filterFunctions.every((filter) => filter(integration)),
      );
    },
    [filters, integrations],
  );

  useEffect(() => {
    setFilteredIntegrations(applyFilters());
  }, [applyFilters]);

  useEffect(() => {
    onSearchTermChange(filters.orgName);
  }, [filters.orgName]);

  const sortHeader = useIntegrationsSortHeader({
    filteredIntegrations,
    setSyncErrors,
  });

  return (
    <PageLayout
      className="integrations-list-page"
      responsiveStyles={responsiveStyles}
      header={
        <PageHeader
          title="Integrations"
          badge={integrationsCount}
          subTitle="Monitor rostering sync status for Clever and Classlink integrations"
        />
      }
    >
      {loadingIntegrations && <Loader fixed></Loader>}

      <div className="filter-container">
        <SearchBox
          placeholder="Search organizations..."
          customOptionLabel={(option) => option.organizationName}
          data={integrations}
          disabled={!integrations}
          onResultSelected={(option) =>
            setFilters((prev) => ({
              ...prev,
              orgName: option.organizationName.toLowerCase(),
            }))
          }
          onSearch={onSearchTermChange}
          onSearchTermChange={onSearchTermChange}
          query={applyFilters}
        />
        <div className="filter-button-group">
          <ProviderDropdown
            onProviderSelected={(provider) =>
              setFilters((prev) => ({ ...prev, provider }))
            }
            selectedProvider={filters.provider}
          />
          <StatusDropdown
            onStatusSelected={(status) =>
              setFilters((prev) => ({ ...prev, status }))
            }
            selectedStatus={filters.status}
          />
        </div>
      </div>

      <SortHeader headers={sortHeader.headers} onSort={sortHeader.onSort} />
      <div className="integration-accordions" ref={containerRef}>
        {filteredIntegrations?.map((integration) => (
          <Accordion
            key={integration.jobExecutionId}
            className="integration-accordion"
          >
            <AccordionHeader>
              <AccordionHeaderName>
                {sortHeader.headers.map((header) => (
                  <div
                    key={header.propName}
                    className={`integration-header-${header.propName}`}
                  >
                    {header.renderer
                      ? header.renderer(integration)
                      : integration[header.propName]}
                  </div>
                ))}
              </AccordionHeaderName>
            </AccordionHeader>
            <AccordionBody className="integration-accordion-body">
              <SyncHistoryItem syncRecord={integration} />
            </AccordionBody>
          </Accordion>
        ))}
      </div>

      {syncErrors && (
        <SyncIntegrationErrorsModal
          open={!!syncErrors}
          onClose={() => setSyncErrors(null)}
          errors={syncErrors}
        />
      )}
    </PageLayout>
  );
};
